import { useQueryClient } from '@tanstack/react-query';
import { memo, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';

import { type FiltersTrackingLocation } from '@air/analytics';
import { useShortIdContext } from '@air/provider-short-id';

import { PublicAudioCodingFilterCard } from '~/components/Filters/AudioCodingFilter/PublicAudioCodingFilterCard';
import { PublicAudioSampleRateFilterCard } from '~/components/Filters/AudioSampleRateFilter/PublicAudioSampleRateFilterCard';
import { PublicCamerasFilterCard } from '~/components/Filters/CamerasFilter/PublicCamerasFilterCard';
import { PublicCityFilterCard } from '~/components/Filters/CityFilter/PublicCityFilterCard';
import { ColorFilterCard, type ColorFilterCardProps } from '~/components/Filters/ColorFilter/ColorFilterCard';
import {
  PublicCopyrightFilterCard,
  type PublicCopyrightFilterCardProps,
} from '~/components/Filters/CopyrightFilter/PublicCopyrightFilterCard';
import { PublicCountryFilterCard } from '~/components/Filters/CountryFilter/PublicCountryFilterCard';
import {
  PublicCreatorFilterCard,
  type PublicCreatorFilterCardProps,
} from '~/components/Filters/CreatorFilter/PublicCreatorFilterCard';
import {
  PublicCustomFieldFilterCard,
  type PublicCustomFieldFilterCardProps,
} from '~/components/Filters/CustomFieldFilter/PublicCustomFieldFilterCard';
import { PublicExtensionsFilterCard } from '~/components/Filters/ExtensionFilter/PublicExtensionsFilterCard';
import { type BaseFilterCardProps } from '~/components/Filters/FilterCard';
import { FiltersDropdown, type FiltersDropdownProps } from '~/components/Filters/FiltersDropdown/FiltersDropdown';
import { OrientationFilterCard } from '~/components/Filters/OrientationFilter/OrientationFilterCard';
import { PublicSourceFilterCard } from '~/components/Filters/SourceFilter/PublicSourceFilterCard';
import { PublicStateFilterCard } from '~/components/Filters/StateFilter/PublicStateFilterCard';
import { PublicVideoAspectRatioFilterCard } from '~/components/Filters/VideoAspectRatioFilter/PublicVideoAspectRatioFilterCard';
import { PublicVideoFrameRateFilterCard } from '~/components/Filters/VideoFrameRateFilter/PublicVideoFrameRateFilterCard';
import { useDidFilterChange } from '~/components/SavedFilter/shared/hooks/useDidFilterChange';
import { useAppliedCustomFieldsFilters } from '~/hooks/filters/useAppliedCustomFieldsFilters';
import { useAppliedTagsFilters } from '~/hooks/filters/useAppliedTagsFilters';
import { usePublicCustomFieldsContext } from '~/providers/PublicCustomFieldsProvider';
import { centralizedBoardIdSelector } from '~/store/centralizedBoard/selectors';
import { getPublicFiltersOptionsKey, useGetPublicFilterOptions } from '~/swr-hooks/filters/useGetPublicFilterOptions';
import { useGetPublicFiltersEntities } from '~/swr-hooks/filters/useGetPublicFiltersEntities';

export type PublicFiltersDropdownProps = {
  trackLocation: FiltersTrackingLocation;
} & Pick<
  FiltersDropdownProps,
  | 'otherFilters'
  | 'availableFilters'
  | 'disabledFilters'
  | 'onApplyClick'
  | 'title'
  | 'customFields'
  | 'availableTypes'
  | 'secondaryCTA'
>;

export const PublicFiltersDropdown = memo((props: PublicFiltersDropdownProps) => {
  const boardId = useSelector(centralizedBoardIdSelector);
  const { data: filterOptions } = useGetPublicFilterOptions({ boardId });
  const { shortId } = useShortIdContext();
  const queryClient = useQueryClient();

  useEffect(() => {
    void queryClient.invalidateQueries({
      queryKey: getPublicFiltersOptionsKey(shortId, boardId),
    });
  }, [queryClient, boardId, shortId]);

  const { appliedTagsFilters } = useAppliedTagsFilters();
  const { appliedCustomFieldsFilters } = useAppliedCustomFieldsFilters();
  const { data: entities } = useGetPublicFiltersEntities({
    tagLabels: appliedTagsFilters,
    customFields: appliedCustomFieldsFilters,
  });
  const { customFields, isReady } = usePublicCustomFieldsContext();
  const { isFilterChanged } = useDidFilterChange({
    options: filterOptions,
    entities,
    catalogCustomFields: isReady ? (customFields ?? []) : undefined,
  });

  const renderColorFilterCard = useCallback(
    (props: Omit<ColorFilterCardProps, 'colors'>) => (
      <ColorFilterCard colors={filterOptions?.colors ?? []} {...props} />
    ),
    [filterOptions?.colors],
  );

  const renderSourceFilterCard = useCallback(
    (props: BaseFilterCardProps) => <PublicSourceFilterCard boardId={boardId} {...props} />,
    [boardId],
  );

  const renderExtensionFilterCard = useCallback(
    (props: BaseFilterCardProps) => <PublicExtensionsFilterCard boardId={boardId} {...props} />,
    [boardId],
  );

  const renderCustomFieldCard = useCallback(
    (props: Omit<PublicCustomFieldFilterCardProps, 'CustomFieldValuesFilterSelect'>) => (
      <PublicCustomFieldFilterCard {...props} />
    ),
    [],
  );

  const renderCopyrightFilterCard = useCallback(
    (props: Omit<PublicCopyrightFilterCardProps, 'boardId'>) => (
      <PublicCopyrightFilterCard boardId={boardId} {...props} />
    ),
    [boardId],
  );

  const renderCreatorFilterCard = useCallback(
    (props: Omit<PublicCreatorFilterCardProps, 'boardId'>) => <PublicCreatorFilterCard boardId={boardId} {...props} />,
    [boardId],
  );

  const renderCamerasFilterCard = useCallback(
    (props: BaseFilterCardProps) => <PublicCamerasFilterCard boardId={boardId} {...props} />,
    [boardId],
  );

  const renderVideoAspectRatioFilterCard = useCallback(
    (props: BaseFilterCardProps) => <PublicVideoAspectRatioFilterCard boardId={boardId} {...props} />,
    [boardId],
  );

  const renderCountryFilterCard = useCallback(
    (props: BaseFilterCardProps) => <PublicCountryFilterCard boardId={boardId} {...props} />,
    [boardId],
  );

  const renderCityFilterCard = useCallback(
    (props: BaseFilterCardProps) => <PublicCityFilterCard boardId={boardId} {...props} />,
    [boardId],
  );

  const renderStateFilterCard = useCallback(
    (props: BaseFilterCardProps) => <PublicStateFilterCard boardId={boardId} {...props} />,
    [boardId],
  );

  const renderVideoFrameRateFilterCard = useCallback(
    (props: BaseFilterCardProps) => <PublicVideoFrameRateFilterCard boardId={boardId} {...props} />,
    [boardId],
  );

  const renderAudioCodingFilterCard = useCallback(
    (props: BaseFilterCardProps) => <PublicAudioCodingFilterCard boardId={boardId} {...props} />,
    [boardId],
  );

  const renderAudioSampleRateFilterCard = useCallback(
    (props: BaseFilterCardProps) => <PublicAudioSampleRateFilterCard boardId={boardId} {...props} />,
    [boardId],
  );

  const renderOrientationFilterCard = useCallback(
    (props: BaseFilterCardProps) => <OrientationFilterCard {...props} />,
    [],
  );

  return (
    <FiltersDropdown
      OrientationFilterCard={renderOrientationFilterCard}
      VideoFrameRateFilterCard={renderVideoFrameRateFilterCard}
      VideoAspectRatioFilterCard={renderVideoAspectRatioFilterCard}
      CreatorFilterCard={renderCreatorFilterCard}
      CountryFilterCard={renderCountryFilterCard}
      CityFilterCard={renderCityFilterCard}
      StateFilterCard={renderStateFilterCard}
      AudioCodingFilterCard={renderAudioCodingFilterCard}
      AudioSampleRateFilterCard={renderAudioSampleRateFilterCard}
      CameraFilterCard={renderCamerasFilterCard}
      ColorFilterCard={renderColorFilterCard}
      CopyrightFilterCard={renderCopyrightFilterCard}
      SourceFilterCard={renderSourceFilterCard}
      CustomFieldFilterCard={renderCustomFieldCard}
      ExtensionFilterCard={renderExtensionFilterCard}
      isFilterChanged={isFilterChanged}
      {...props}
    />
  );
});

PublicFiltersDropdown.displayName = 'PublicFiltersDropdown';
